import { IPostCheckoutCharge } from "@api-clients/charges";
import { TCapiAddToCartCustomData } from "@capi/types";
import type DecimalType from "decimal.js";

import { Decimal, toCurrencyDecimalsNumber } from "@/decimal-util";

import {
  ACCEPT_COOKIES_STORAGE_KEY,
} from "apps/website/components/page/CookieBar/CookieBar.map";

import { buildGtmDataGA4 } from "./gtm-builders";

/**
 * Segment ecommerce spec event names. The Segment Reddit Pixel (Actions)
 * destination triggers its Add to Cart / Purchase mappings on these.
 * https://segment.com/docs/connections/spec/ecommerce/v2/
 */
export const SEGMENT_EVENT_ORDER_UPDATED = "Order Updated";
export const SEGMENT_EVENT_ORDER_COMPLETED = "Order Completed";

/**
 * Whether the visitor accepted the cookie banner (see CookieBar).
 * Segment marketing destinations must only be sent events after consent.
 */
export function hasAcceptedCookies(): boolean {
  try {
    return globalThis.localStorage?.getItem(ACCEPT_COOKIES_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * One cart-level payload per add to cart action, with every item in
 * products, so a customer who picks more recipes doesn't send more events to
 * the ad platform. Deliberately excludes customer identifying data (email,
 * name, phone, address). Identity reaches Segment through analytics.identify
 * instead.
 */
export function buildSegmentOrderUpdated(customData: TCapiAddToCartCustomData) {
  return {
    currency: customData.currency.toUpperCase(),
    total: customData.value,
    coupon: customData.searchString,
    products: customData.contents.map((item) => ({
      product_id: item.id,
      name: item.title,
      category: item.category,
      brand: item.brand,
      price: item.itemPrice,
      quantity: item.quantity,
    })),
  };
}

/**
 * Segment spec: revenue is the transaction value including discounts but
 * excluding shipping and tax, whereas total includes them.
 */
export function calculateSegmentRevenue(
  total: DecimalType.Value,
  shipping: DecimalType.Value,
  tax: DecimalType.Value,
): number {
  return toCurrencyDecimalsNumber(
    new Decimal(total).minus(shipping).minus(tax),
  );
}

/**
 * Deliberately excludes customer identifying data (email, name, phone,
 * address) and the product list: the Reddit mapping only needs the order
 * totals, and the purchase item ids differ from the add to cart ids.
 * order_id is the charge id so a future server-side event can use
 * the same id for deduplication.
 */
export function buildSegmentOrderCompleted(charge: IPostCheckoutCharge) {
  const { ecommerce } = buildGtmDataGA4(charge);
  const total = Number(ecommerce.value);
  const shipping = Number(ecommerce.shipping);
  const tax = Number(ecommerce.tax);

  return {
    order_id: ecommerce.transaction_id,
    currency: ecommerce.currency,
    total,
    revenue: calculateSegmentRevenue(
      ecommerce.value,
      ecommerce.shipping,
      ecommerce.tax,
    ),
    shipping,
    tax,
    coupon: ecommerce.coupon,
  };
}
